import { LivermpMarketplaceAPIKey } from '~/src/dash/api/liveramp-segments/liveramp_models';
import { ILiverampMarketplaceSegmentsListInfo } from '~/src/dash/api/liveramp-segments/liveramp_models';
import {
  formatSelectedToArrayOfStrings,
  ICustomAPIFilterConfig,
} from '~/src/dash/components/filters/common';
import {
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  CurrencyRender,
  EllipsisWithTooltipRender,
  RequestStatus,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';

export const formatLiverampMarketplaceSegmentsResponseIntoLVTRows = (
  segments: ILiverampMarketplaceSegmentsListInfo[],
  cfg: ILiverampMarketplaceSegmentListViewTable,
): IListViewTableRow[] => {
  return segments.map((segment) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = segment[responseKey as keyof ILiverampMarketplaceSegmentsListInfo];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });

    return formattedRow;
  });
};

interface ILiverampMarketplaceSegmentListViewTable {
  columns: IListViewTableColumns;
}

interface ILiverampSegmentListViewConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys?: LivermpMarketplaceAPIKey[];
    isTableExpanded?: boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  columnSelector: {
    resetKeys?: LivermpMarketplaceAPIKey[]; // what keys should be made active on reset
  };
  filters: ICustomAPIFilterConfig;
  isInitialized?: boolean;
  sorts: LivermpMarketplaceAPIKey[];
  table: ILiverampMarketplaceSegmentListViewTable;
  tabKey: string;
}

export const LiverampDataMarketplaceConfig: ILiverampSegmentListViewConfig = {
  tabKey: 'All',
  defaultState: {
    activeColumnKeys: ['IsRequested', 'ProviderName', 'ReachIOSCount'],
    isTableExpanded: true,
    order: 'desc',
    orderBy: '',
    page: 0,
    rowsPerPage: 10,
  },
  columnSelector: {
    resetKeys: ['IsRequested', 'ProviderName', 'ReachIOSCount'],
  },
  filters: {
    // TODO: BLOCKER FROM LIVERAMP
    IsRequested: {
      queryKey: 'IsRequested',
      formatRequestValue: formatSelectedToArrayOfStrings,
      defaultSelected: ['0'],
    },
    'Segment Type': {
      queryKey: 'segmentType',
      formatRequestValue: formatSelectedToArrayOfStrings,
      hasSelectAll: true,
    },
    Providers: {
      queryKey: 'Providers',
      formatRequestValue: formatSelectedToArrayOfStrings,
      isMultiSelectDisabled: false,
      hasSelectAll: true,
    },
    'Use Cases': {
      queryKey: 'useCases',
      formatRequestValue: formatSelectedToArrayOfStrings,
      hasSelectAll: true,
    },
  },
  sorts: ['Name'],
  table: {
    columns: {
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'SegmentID',
        responseKey: 'SegmentID',
        valueType: 'string',
      },
      Name: {
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name_link',
        headerRenderType: 'name',
        defaultOrderDirection: 'asc',
        isSearchable: true,
        key: 'Name',
        label: 'Segment Name',
        requestKey: 'Name',
        responseKey: 'Name',
        valueType: 'string',
      },
      ProviderName: {
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        key: 'ProviderName',
        label: 'Provider',
        requestKey: 'ProviderName',
        responseKey: 'ProviderName',
        valueType: 'string',
        disableSort: true,
      },
      PricingRevenueShareAmount: {
        // AdvertiserDirect%OfMedia'
        formatValue: (value: string) => value + '%',
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        key: 'PricingRevenueShareAmount',
        label: 'Advertiser Direct % of Media',
        requestKey: 'PricingRevenueShareAmount',
        responseKey: 'PricingRevenueShareAmount',
        orderByKey: 'pricing_revenue_share_amount',
        valueType: 'string',
      },
      PricingDigitalAdTargetingAmount: {
        // CPM
        cellJSX: CurrencyRender,
        formatValue: (value: number) => value / 100,
        cellRenderType: 'number',
        defaultOrderDirection: 'asc',
        headerRenderType: 'number',
        key: 'PricingDigitalAdTargetingAmount',
        label: 'CPM',
        requestKey: 'PricingDigitalAdTargetingAmount',
        responseKey: 'PricingDigitalAdTargetingAmount',
        orderByKey: 'pricing_digital_ad_targeting_amount',
        valueType: 'number',
      },
      ReachIOSCount: {
        cellRenderType: 'number',
        defaultOrderDirection: 'asc',
        headerRenderType: 'number',
        isSearchable: true,
        key: 'ReachIOSCount',
        label: 'IOS Reach',
        requestKey: 'ReachIOSCount',
        responseKey: 'ReachIOSCount',
        orderByKey: 'reach_ios_count',
        valueType: 'number',
        disableSort: false,
      },
      ReachAndroidCount: {
        cellRenderType: 'number',
        defaultOrderDirection: 'asc',
        headerRenderType: 'number',
        isSearchable: true,
        key: 'ReachAndroidCount',
        label: 'Android Reach',
        requestKey: 'ReachAndroidCount',
        responseKey: 'ReachAndroidCount',
        orderByKey: 'reach_android_count',
        valueType: 'number',
      },
      Actions: {
        cellRenderType: 'actions',
        disableSort: true,
        headerRenderType: 'actions',
        key: 'Actions',
        label: 'Actions',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
      IsRequested: {
        cellJSX: RequestStatus,
        cellRenderType: 'text_center',
        defaultOrderDirection: 'asc',
        key: 'IsRequested',
        label: 'Request Status',
        requestKey: 'IsRequested',
        responseKey: 'IsRequested',
        valueType: 'string',
        disableSort: true,
      },
      SegmentType: {
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        key: 'SegmentType',
        label: 'Segment Type',
        requestKey: 'SegmentType',
        responseKey: 'SegmentType',
        valueType: 'string',
        disableSort: true,
      },
    },
  },
  cypressClassName: '',
};
